// 回收品类相关类型
export interface RecycleCategory {
  id: number;
  categoryName: string;
  parentId?: number;
  description?: string;
  basePrice?: number;
  basePictureUrl?: string;
  sort: number;
  level: number;
  enable: boolean;
  valuationTemplateName?: string;
  valuationDescription?: string;
  valuationEnable: boolean;
  children?: RecycleCategory[];
  createdTime: string;
  updatedTime: string;
}

// 回收订单配置项类型
export interface RecycleOrderConfigItem {
  configItemId: number;
  configName: string;
  configItemName: string;
  valuationCoefficient: string;
  guidePictureUrl?: string;
}

// 回收订单相关类型
export interface RecycleOrder {
  id: number;
  orderNo: string;
  userId: number;
  // 新增字段：用户信息
  userNickname?: string;
  userPhone?: string;
  categoryId: number;
  categoryName?: string;
  onlineQuote: number;
  finalQuote?: number;
  status: RecycleOrderStatus;
  sendOutMethod?: RecycleOrderSendOutMethod;
  recycleProductImgUrl?: string;
  remark?: string;
  // 新增字段：寄出的物流单号
  outboundTrackingNo?: string;
  // 新增字段：物流公司
  logisticsCompany?: string;
  // 新增字段：寄出物流记录ID（用于查询物流轨迹）
  outboundLogisticId?: number;
  // 新增字段：用户选择的配置项信息
  selectedConfigItems?: RecycleOrderConfigItem[];
  // 新增字段：寄送地址和预约时间信息
  visitAddress?: string;
  visitProvince?: string;
  visitCity?: string;
  visitCounty?: string;
  visitContactName?: string;
  visitContactPhone?: string;
  visitTime?: string;
  createdTime: string;
  updatedTime: string;
}

// 回收物流相关类型
export interface RecycleLogistic {
  id: number;
  orderId: number;
  orderNo?: string;
  logisticsCompany: string;
  trackingNo?: string;
  shipmentStatus: RecycleShipmentStatus;
  shipTime?: string;
  deliveryTime?: string;
  shippingCost?: number;
  routeInfo?: string;
  postageSettlementMethod?: string;
  outbound: boolean;
  visitAddress?: string;
  visitProvince?: string;
  visitCity?: string;
  visitCounty?: string;
  visitContactName?: string;
  visitContactPhone?: string;
  visitTime?: string;
  remark?: string;
  createdTime: string;
  updatedTime: string;
}

// 回收估价配置相关类型
export interface RecycleValuationConfiguration {
  id: number;
  categoryId: number;
  categoryName?: string;
  configurationName: string;
  valuationWeight: number;
  sort: number;
  enable: boolean;
  isMultipleChoice?: boolean;
  items?: RecycleValuationConfigurationItem[];
  createdTime?: string;
  updatedTime?: string;
}

export interface RecycleValuationConfigurationItem {
  id: number;
  configurationId: number;
  itemName: string;
  valuationCoefficient: number;
  guidePictureUrl?: string;
  description?: string;
  sort: number;
  enable: boolean;
  createdTime?: string;
  updatedTime?: string;
}

// 物流跟踪相关类型
export interface LogisticTrackingDTO {
  logisticId: number;
  trackingNo: string;
  logisticsCompany: string;
  currentStatus: string;
  lastUpdateTime: string;
  trackingSteps: TrackingStepDTO[];
}

export interface TrackingStepDTO {
  status: string;
  description: string;
  time: string;
  location: string;
}

// 枚举类型
export enum RecycleOrderStatus {
  PENDING_REVIEW = 'PENDING_REVIEW',
  PENDING_SENDOUT = 'PENDING_SENDOUT',
  SENDING_OUT = 'SENDING_OUT',
  PENDING_ASSESS = 'PENDING_ASSESS',
  PENDING_CONFIRM = 'PENDING_CONFIRM',
  CONFIRMED = 'CONFIRMED',
  PENDING_SEND_BACK = 'PENDING_SEND_BACK',
  SENDING_BACK = 'SENDING_BACK',
  PENDING_SETTLEMENT = 'PENDING_SETTLEMENT',
  SETTLED = 'SETTLED',
  CLOSED = 'CLOSED',
  CANCELED = 'CANCELED',
  CANCELING = 'CANCELING'
}

export enum RecycleOrderSendOutMethod {
  SELF_SEND = 'SELF_SEND',
  LOGISTICS_PICKUP = 'LOGISTICS_PICKUP',
  HOME_VISIT = 'HOME_VISIT'
}

export enum RecycleShipmentStatus {
  INIT = 'INIT',
  LOGISTICS_PENDING = 'LOGISTICS_PENDING',
  LOGISTICS_DELIVERED = 'LOGISTICS_DELIVERED',
  LOGISTICS_DISPATCHING = 'LOGISTICS_DISPATCHING',
  LOGISTICS_SIGNED = 'LOGISTICS_SIGNED',
  LOGISTICS_EXCEPTION = 'LOGISTICS_EXCEPTION',
  LOGISTICS_CANCELED = 'LOGISTICS_CANCELED'
}

// 查询参数类型
export interface RecycleCategoryQuery {
  categoryName?: string;
  parentId?: number;
  enable?: boolean;
  valuationEnable?: boolean;
  keyword?: string;
  page: number;
  size: number;
}

export interface RecycleOrderQuery {
  orderNo?: string;
  status?: string;
  createdTimeStart?: string;
  createdTimeEnd?: string;
  page: number;
  size: number;
}

export interface RecycleLogisticQuery {
  orderId?: number;
  orderNo?: string;
  trackingNo?: string;
  logisticsCompany?: string;
  shipmentStatus?: string;
  outbound?: boolean;
  page: number;
  size: number;
}

export interface RecycleValuationQuery {
  categoryId?: number;
  configurationName?: string;
  enable?: boolean;
  page: number;
  size: number;
}

// 创建和更新命令类型
export interface CreateRecycleCategoryCommand {
  categoryName: string;
  parentId?: number;
  description?: string;
  basePrice?: number;
  basePictureUrl?: string;
  sort: number;
  level: number;
  valuationTemplateName?: string;
  valuationDescription?: string;
  valuationEnable: boolean;
  enable?: boolean;
}

export interface UpdateRecycleCategoryCommand {
  id: number;
  categoryName: string;
  parentId?: number;
  description?: string;
  basePrice?: number;
  basePictureUrl?: string;
  sort: number;
  level: number;
  valuationTemplateName?: string;
  valuationDescription?: string;
  valuationEnable: boolean;
  enable?: boolean;
}

export interface CreateRecycleValuationConfigurationCommand {
  categoryId: number;
  configurationName: string;
  valuationWeight: number;
  sort: number;
  enable: boolean;
  items?: {
    itemName: string;
    valuationCoefficient: number;
    sort: number;
    enable: boolean;
  }[];
}

export interface UpdateRecycleValuationConfigurationCommand {
  id: number;
  categoryId: number;
  configurationName: string;
  valuationWeight: number;
  sort: number;
  enable: boolean;
  items?: {
    itemName: string;
    valuationCoefficient: number;
    sort: number;
    enable: boolean;
  }[];
}

export interface AddTrackingNumberCommand {
  logisticId: number;
  trackingNo: string;
  logisticsCompany: string;
}

export interface CreateReturnLogisticCommand {
  orderId: number;
  logisticsCompany: string;
  trackingNo: string;
  visitAddress: string;
  visitContactName: string;
  visitContactPhone: string;
  remark?: string;
}

export interface SettleOrderCommand {
  orderNo: string;
  finalQuote: number;
  remark?: string;
}

export interface CancelOrderCommand {
  orderNo: string;
  cancelReason: string;
}

export interface SubmitFinalQuoteCommand {
  finalQuote: number;
  remark?: string;
}

export interface ConfirmFinalQuoteCommand {
  accepted: boolean;
  remark?: string;
}

// 结算记录相关类型
export interface RecycleSettlementRecord {
  id: number;
  orderId: number;
  tradeNo?: string;
  paymentAccount?: string;
  paymentChannel: string;
  settlementStatus: string;
  settlementType: string;
  dueAmount: number;
  actualAmount: number;
  expectSettleTime?: string;
  settledTime?: string;
  remark?: string;
  failReason?: string;
  createdTime: string;
  updatedTime: string;
}

export interface CreateSettlementRecordCommand {
  orderId: number;
  dueAmount: number;
  actualAmount: number;
  settlementType: string;
  paymentChannel: string;
  paymentAccount?: string;
  tradeNo?: string;
  expectSettleTime?: string;
  remark?: string;
}

// 质检报告相关类型
export interface RecycleQualification {
  id: number;
  orderId: number;
  testingResult?: string;
  exceptionReason?: string;
  remark?: string;
  items: RecycleQualificationItem[];
  createdTime: string;
  updatedTime: string;
}

export interface RecycleQualificationItem {
  id: number;
  qualificationId: number;
  itemName: string;
  occurException: boolean;
  exceptionReason?: string;
  testingResult?: string;
  repairCost?: number;
  remark?: string;
  createdTime: string;
  updatedTime: string;
}

export interface CreateRecycleQualificationCommand {
  orderId: number;
  testingResult?: string;
  exceptionReason?: string;
  remark?: string;
  items: CreateRecycleQualificationItemCommand[];
}

export interface CreateRecycleQualificationItemCommand {
  itemName: string;
  occurException: boolean;
  exceptionReason?: string;
  testingResult?: string;
  repairCost?: number;
  remark?: string;
}

export interface UpdateRecycleQualificationCommand {
  id: number;
  testingResult?: string;
  exceptionReason?: string;
  remark?: string;
  items: UpdateRecycleQualificationItemCommand[];
}

export interface UpdateRecycleQualificationItemCommand {
  id?: number;
  itemName: string;
  occurException: boolean;
  exceptionReason?: string;
  testingResult?: string;
  repairCost?: number;
  remark?: string;
}

export interface RecycleQualificationQuery {
  orderId?: number;
  startTime?: string;
  endTime?: string;
  page: number;
  size: number;
}

// 状态管理相关类型
export interface RecycleState {
  categories: {
    list: RecycleCategory[];
    total: number;
    loading: boolean;
    error: string | null;
  };
  orders: {
    list: RecycleOrder[];
    total: number;
    loading: boolean;
    error: string | null;
  };
  logistics: {
    list: RecycleLogistic[];
    total: number;
    loading: boolean;
    error: string | null;
  };
  valuations: {
    list: RecycleValuationConfiguration[];
    total: number;
    loading: boolean;
    error: string | null;
  };
}
